import { Injectable, Type } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { IDynamicComponentConfig } from '@core/directives/dynamic-host.directive';

export interface INotification {
  component?: IDynamicComponentConfig;
  body?: string;
  insertedAt: string;
  id: string;
  removeFn: () => void;
  read: boolean;
}

export interface INotificationConfig extends INotification {
  beforeRemoveFn: () => void;
}

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly _notifications$ = new BehaviorSubject([]);
  private readonly _openTrigger$ = new Subject<void>();

  readonly notifications$: Observable<INotification[]> = this._notifications$.asObservable();

  readonly openTrigger$ = this._openTrigger$.asObservable();
  readonly notificationCount$ = this.notifications$.pipe(map(({ length }) => length));
  readonly unreadCount$ = this.notifications$.pipe(map((ns) => ns.filter(n => !n.read).length));

  readonly allIsRead$ = this.unreadCount$.pipe(map((count) => count === 0));
  readonly isEmpty$ = this.notificationCount$.pipe(map((count) => count === 0));
  readonly badgeHidden$ = combineLatest([
    this.allIsRead$,
    this.isEmpty$
  ]).pipe(map(data => data.some(Boolean)));


  /**
   * @param notify
   * @param openAfterInsert
   * @return notification id
   */
  insert(notify: Partial<Omit<INotificationConfig, 'removeFn'>>, openAfterInsert = false): string {
    const model: INotification = {
      ...notify,
      id: notify.id ?? uuidv4(),
      insertedAt: new Date().toUTCString(),
      read: false,
      removeFn: () => {
        if (notify.beforeRemoveFn) {
          notify.beforeRemoveFn();
        }
        this.remove(model.id);
      },
    };

    this._notifications$.next([model, ...this._notifications$.value]);

    if (openAfterInsert) {
      this._openTrigger$.next();
    }

    return model.id;
  }

  markAsUnread(id: string): void {
    const notifications: INotification[] = this._notifications$.value.map((notify: INotification) => {
      if (notify.id === id) {
        return { ...notify, read: false };
      }
      return notify;
    });
    this._notifications$.next(notifications);
  }

  markAllAsRead(): void {
    const notifications: INotification[] = this._notifications$.value.map((notify) => ({ ...notify, read: true }));
    this._notifications$.next(notifications);
  }

  private remove(notifyId: string): void {
    const filtered = this._notifications$.value.filter(({ id }) => id !== notifyId);
    this._notifications$.next(filtered);
  }
}
